// React
import { useContext, useEffect, useState } from "react"

// Components
import { ActionButton, ImportList } from "../../components"

// Contexts
import { DataContext, MasterContext } from "../contexts"

// Form
import { ValidationError } from "yup"

// Types
import type { ErrorFetch, SuccessFetch } from "types"
import type { DataType } from "../../types"

// Utils
import { useApi } from "utils"
import { getFinalMessage, useRowStatus } from "../../utils"
import { productValidationSchema } from "../utils"

export function ImportData(): JSX.Element {
  // Hooks
  const { data } = useContext(DataContext)
  const { error, index, success, toggle } = useRowStatus()

  useEffect(() => {
    if (index >= data.length) {
      getFinalMessage(data, error, success)
    }

    // eslint-disable-next-line
  }, [index])

  return (
    <ActionButton
      data={data}
      error={error}
      index={index}
      onStartImport={toggle}
    >
      {[...Array(data.length)].map((_, key) => {
        // Vars
        const item = data[key]

        return (
          <ImportRow
            key={key}
            activeIndex={index}
            data={item}
            error={error.find((item) => key === item.index)?.message}
            index={key}
            onError={(value) => toggle("error", value)}
            onSuccess={() => toggle("success")}
          />
        )
      })}
    </ActionButton>
  )
}

function ImportRow(params: {
  activeIndex: number
  data: DataType
  error?: string[]
  index: number
  onSuccess: () => void
  onError: (value: string[]) => void
}): JSX.Element {
  // Vars
  const { activeIndex, error, index } = params
  const sameIndex: boolean = activeIndex === index
  
  // Hooks
  const api = useApi()
  const masterData = useContext(MasterContext)
  const [data, setData] = useState<DataType>(params.data)

  useEffect(() => {
    if (sameIndex) {
      // Vars
      let error: { [key: string]: string } = {}
      let payload = {
        customer_id: masterData.data.customer.find(i => i.label.toLowerCase() === data.Customer_code?.toLowerCase())?.value ?? undefined,
        Customer_code: data.Customer_code,
        Customer_name: data.Customer_name,
      }

      // Function to validate all products
      const validateProducts = async (): Promise<any> => {
        try {
          await productValidationSchema.validate(
            payload,
            { abortEarly: false }
          )
        } catch (err) {
          if (err instanceof ValidationError) {
            // Create an error object
            const errorObject = err.inner.reduce<Record<string, string>>((acc, curr) => {
              if (curr.path) {
                acc[curr.path] = curr.message
              }
              return acc
            }, {})

            return errorObject
          }
        }

        return null
      }

      // Functions
      const getApi = () => {
        if (payload.customer_id) {
          return api.putForm(
            "/customer/edit",
            payload,
            { params: { customer_id: payload.customer_id } }
          )
        }

        return api.postForm("/customer/add", payload)
      }
      const generateCode = (): Promise<string> => {
        return new Promise<string>((resolve) => {
          if (payload.Customer_code) {
            return resolve(payload.Customer_code)
          }

          api.get(
            "/customer/gencode"
          ).then((res: SuccessFetch<{
            payload: { customer_code: string }
          }>) => {
            resolve(res.data.payload.customer_code)
          }).catch(() => {
            resolve("")
          })
        })
      }
      const saveData = () => {
        getApi().then(() => {
          params.onSuccess()
        }).catch((err: ErrorFetch<{
          message?: string
        }>) => {
          params.onError([err.response.data?.message ?? err.response.message])
        })
      }
      const submitData = async () => {
        // Usage
        generateCode().then(code => {
          if (data.Customer_code !== code) {
            payload.Customer_code = code

            setData(prev => {
              return {
                ...prev,
                Customer_code: code
              }
            })
          }
        }).finally(() => {
          validateProducts().then((errors) => {
            if (errors) {
              error = errors

              return params.onError(Object.keys(error).map(item => error[item]))
            }
    
            saveData()
          })
        })
      }

      submitData()
    }

    // eslint-disable-next-line
  }, [sameIndex])

  return (
    <ImportList
      activeIndex={activeIndex}
      data={data}
      index={index}
      label={data.Customer_name ?? "-"}
      error={error}
    />
  )
}